import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Loader2, Search, ShieldCheck, Landmark } from 'lucide-react';
import { toast } from 'sonner';

const MODULE_LABELS = [
  { key: 'my_schedule', label: 'My Schedule', desc: 'Personal schedule, hours, pay period' },
  { key: 'requests', label: 'Requests', desc: 'Time off + shift swap requests' },
  { key: 'notifications', label: 'Notifications', desc: 'Notification bell + announcements' },
  { key: 'policies', label: 'Policies', desc: 'Read company policies' },
  { key: 'profile', label: 'Profile', desc: 'Edit name / contact info' },
];

export default function StaffAccessTab({ api }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingFor, setSavingFor] = useState(null);
  const [portalUsers, setPortalUsers] = useState([]);
  const [portalLoading, setPortalLoading] = useState(true);

  const fetchPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.get('/portal-access');
      setPortalUsers(data);
    } catch {
      toast.error('Failed to load portal access');
    }
    setPortalLoading(false);
  }, [api]);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);

  const togglePortal = async (userId, flag, value) => {
    const target = portalUsers.find(u => u.id === userId);
    if (!target) return;
    setPortalUsers(prev => prev.map(u => u.id === userId ? { ...u, [flag]: value } : u));
    try {
      await api.put(`/portal-access/${userId}`, { flag, value });
      toast.success(`${target.name}: ${flag === 'finance_access' ? 'Finance' : 'Board Portal'} ${value ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Save failed');
      setPortalUsers(prev => prev.map(u => u.id === userId ? target : u));
    }
  };

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/staff-access');
      setStaff(data);
    } catch {
      toast.error('Failed to load staff access');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.role || '').toLowerCase().includes(q));
  }, [staff, search]);

  const toggleModule = async (userId, moduleKey, value) => {
    setSavingFor(userId);
    const target = staff.find(s => s.id === userId);
    if (!target) return;
    const next = { ...target.module_access, [moduleKey]: value };
    // Optimistic update
    setStaff(prev => prev.map(s => s.id === userId ? { ...s, module_access: next } : s));
    try {
      await api.put(`/staff-access/${userId}`, { module_access: next });
      toast.success(`${target.name}: ${moduleKey.replace(/_/g, ' ')} ${value ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Save failed');
      // Revert
      setStaff(prev => prev.map(s => s.id === userId ? target : s));
    }
    setSavingFor(null);
  };

  return (
    <div className="space-y-4" data-testid="staff-access-tab">
      <Card className="border-[#DED8EB] shadow-sm" data-testid="portal-access-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Landmark className="h-4 w-4 text-[#6B3FA0]" /> Portal Access — Finance &amp; Board
          </CardTitle>
          <p className="text-sm text-[#5A4D6E]">Control which admins and supervisors can see the Finance and Board Portal sections. Changes take effect on their next page refresh and are recorded in the audit log.</p>
        </CardHeader>
        <CardContent>
          {portalLoading ? (
            <div className="flex items-center justify-center py-8 text-[#8E85A0]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="rounded-lg border border-[#DED8EB] overflow-hidden">
              <table className="w-full text-sm" data-testid="portal-access-table">
                <thead className="bg-[#F7F4EF] text-[10px] uppercase tracking-wider text-[#5A4D6E]">
                  <tr>
                    <th className="text-left p-2 font-semibold">User</th>
                    <th className="text-center p-2 font-semibold">Finance</th>
                    <th className="text-center p-2 font-semibold">Board Portal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DED8EB]">
                  {portalUsers.map(u => (
                    <tr key={u.id} className="hover:bg-[#F7F4EF]/50">
                      <td className="p-2">
                        <div className="font-semibold text-[#2D1B4E]">{u.name}</div>
                        <div className="text-[11px] text-[#8E85A0]">{u.email}</div>
                        <Badge className="mt-1 text-[10px] bg-[#EDE7F6] text-[#5A4D6E] border-0">{u.role}</Badge>
                      </td>
                      <td className="text-center p-2">
                        <Switch
                          checked={u.finance_access}
                          onCheckedChange={(v) => togglePortal(u.id, 'finance_access', v)}
                          data-testid={`portal-finance-${u.id}`}
                        />
                      </td>
                      <td className="text-center p-2">
                        <Switch
                          checked={u.board_access}
                          onCheckedChange={(v) => togglePortal(u.id, 'board_access', v)}
                          data-testid={`portal-board-${u.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                  {portalUsers.length === 0 && (
                    <tr><td colSpan={3} className="p-4 text-center text-[#8E85A0]">No admins or supervisors found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ShieldCheck className="h-4 w-4 text-[#6B3FA0]" /> Staff Module Access
          </CardTitle>
          <p className="text-sm text-[#5A4D6E]">Control which sections each staff member sees in their app. Toggles save instantly and are recorded in the audit log.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[#8E85A0]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, or role…"
              className="pl-7 border-[#DED8EB] h-8 text-sm"
              data-testid="staff-access-search"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-[#8E85A0]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-[#8E85A0] py-6 text-center">No staff match.</p>
          ) : (
            <div className="rounded-lg border border-[#DED8EB] overflow-hidden">
              <table className="w-full text-sm" data-testid="staff-access-table">
                <thead className="bg-[#F7F4EF] text-[10px] uppercase tracking-wider text-[#5A4D6E]">
                  <tr>
                    <th className="text-left p-2 font-semibold">Staff</th>
                    {MODULE_LABELS.map(m => (
                      <th key={m.key} className="text-center p-2 font-semibold" title={m.desc}>{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#DED8EB]">
                  {filtered.map(s => (
                    <tr key={s.id} className="hover:bg-[#F7F4EF]/50">
                      <td className="p-2">
                        <div className="font-semibold text-[#2D1B4E]">{s.name}{savingFor === s.id && <Loader2 className="h-3 w-3 animate-spin inline ml-2" />}</div>
                        <div className="text-[11px] text-[#8E85A0]">{s.email}</div>
                        <Badge className="mt-1 text-[10px] bg-[#EDE7F6] text-[#5A4D6E] border-0">{s.role}</Badge>
                      </td>
                      {MODULE_LABELS.map(m => (
                        <td key={m.key} className="text-center p-2">
                          <Switch
                            checked={!!s.module_access?.[m.key]}
                            onCheckedChange={(v) => toggleModule(s.id, m.key, v)}
                            data-testid={`access-${s.id}-${m.key}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
